// src/components/sliders/SlidersManagement.js
import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Table, Modal } from "react-bootstrap";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaImages,
  FaSyncAlt,
  FaExternalLinkAlt
} from "react-icons/fa";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  orderBy,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../../firebase";
import UploadSliderModal from "./UploadSliderModal";
import EditSliderModal from "./EditSliderModal";
import DeleteSliderConfirmModal from "./DeleteSliderConfirmModal";

// Self-contained in-page toast matching Blacklist.js / FraudRules.js conventions
const Toast = ({ toast }) => {
  if (!toast) return null;
  const isError = toast.type === "error";
  return (
    <div
      style={{
        position: "fixed",
        bottom: 28,
        right: 28,
        zIndex: 2100,
        background: isError ? "rgba(239,68,68,0.92)" : "rgba(34,197,94,0.92)",
        backdropFilter: "blur(8px)",
        color: "#fff",
        borderRadius: 10,
        padding: "12px 20px",
        fontSize: 13,
        fontWeight: 600,
        boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        gap: 10,
        maxWidth: 380,
        animation: "sliderToastIn 0.25s ease",
      }}
    >
      <span>{isError ? "✗" : "✓"}</span>
      <span>{toast.message}</span>
      <style>{`@keyframes sliderToastIn { from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
};

const SlidersManagement = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all"); // 'all', 'active', 'inactive'
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [deletingSlider, setDeletingSlider] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // Fetch all sliders ordered ascending by sortOrder
  const fetchSliders = useCallback(async () => {
    try {
      setLoading(true);
      const q = query(collection(db, "sliders"), orderBy("sortOrder", "asc"));
      const querySnapshot = await getDocs(q);
      const list = querySnapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setSliders(list);
    } catch (error) {
      console.error("Error fetching sliders:", error);
      showToast("error", "Failed to load sliders from database.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSliders();
  }, [fetchSliders]);

  // Fast inline Active/Inactive toggle
  const handleToggleActive = async (slider) => {
    if (togglingId === slider.id) return;
    const newStatus = !slider.isActive;

    // Optimistic local update
    setSliders((prev) =>
      prev.map((s) => (s.id === slider.id ? { ...s, isActive: newStatus } : s))
    );
    setTogglingId(slider.id);

    try {
      const sliderRef = doc(db, "sliders", slider.id);
      await updateDoc(sliderRef, {
        isActive: newStatus,
        updatedAt: serverTimestamp(),
      });
      showToast(
        "success",
        `Slider #${slider.sortOrder} marked as ${newStatus ? "Active" : "Inactive"}`
      );
    } catch (error) {
      console.error("Error toggling slider status:", error);
      // Revert optimistic update
      setSliders((prev) =>
        prev.map((s) => (s.id === slider.id ? { ...s, isActive: slider.isActive } : s))
      );
      showToast("error", "Failed to update slider status.");
    } finally {
      setTogglingId(null);
    }
  };

  // Filter sliders based on status
  const filteredSliders = sliders.filter((slider) => {
    if (statusFilter === "active") return slider.isActive;
    if (statusFilter === "inactive") return !slider.isActive;
    return true;
  });

  const totalCount = sliders.length;
  const activeCount = sliders.filter((s) => s.isActive).length;
  const inactiveCount = totalCount - activeCount;

  // Format Firestore timestamp
  const formatDate = (val) => {
    if (!val) return "—";
    let date;
    if (typeof val.toDate === "function") {
      date = val.toDate();
    } else if (val.seconds) {
      date = new Date(val.seconds * 1000);
    } else {
      date = new Date(val);
    }
    return isNaN(date.getTime()) ? "—" : date.toLocaleString();
  };

  return (
    <section className="px-3 py-4 nav-body-bg btm-rad">
      <Container fluid>
        {/* Page Header & Actions Bar */}
        <Row className="align-items-center justify-content-between g-3 mb-4">
          <Col xs={12} md="auto">
            <div className="d-flex align-items-center gap-3">
              <div>
                <h2 className="text-h2 mb-1" style={{ color: "var(--text-primary)" }}>
                  Homepage Sliders
                </h2>
                <div className="d-flex align-items-center gap-2 small">
                  <span style={{ color: "var(--text-secondary)" }}>
                    Total: <strong style={{ color: "var(--text-primary)" }}>{totalCount}</strong>
                  </span>
                  <span style={{ color: "var(--divider)" }}>•</span>
                  <span style={{ color: "var(--success)" }}>
                    Active: <strong>{activeCount}</strong>
                  </span>
                  <span style={{ color: "var(--divider)" }}>•</span>
                  <span style={{ color: "var(--danger)" }}>
                    Inactive: <strong>{inactiveCount}</strong>
                  </span>
                </div>
              </div>
            </div>
          </Col>

          {/* Controls: Filter, Refresh, Upload Button */}
          <Col xs={12} md="auto">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Status Filter */}
              <select
                className="form-select theme-sort-select"
                style={{ width: "135px", padding: "6px 12px", borderRadius: 8, fontSize: 13 }}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Sliders</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>

              {/* Refresh Button */}
              <button
                className="btn btn-secondary px-3"
                style={{ padding: "6px 12px", borderRadius: 8, fontSize: 13 }}
                onClick={fetchSliders}
                disabled={loading}
                title="Refresh Sliders"
              >
                <FaSyncAlt className={loading ? "spin-icon" : ""} />
              </button>

              {/* Upload New Slider Button */}
              <button
                id="btn-upload-new-slider"
                className="btn btn-primary px-3 fw-bold"
                style={{
                  padding: "6px 18px",
                  borderRadius: 8,
                  fontSize: 13,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
                onClick={() => setShowUploadModal(true)}
              >
                <FaPlus size={12} />
                <span>Upload New Slider</span>
              </button>
            </div>
          </Col>
        </Row>

        {/* Table View */}
        <div
          className="table-responsive scrollable-table-y mt-2 bg-transparent"
          style={{ minHeight: "60vh", overflowY: "auto" }}
        >
          <Table className="table-hover align-middle body-text-two mb-0">
            <thead className="sticky-top top-0" style={{ zIndex: 10 }}>
              <tr>
                <th className="transparent-header text-center text-white fw-bold" style={{ width: 60 }}>
                  Order
                </th>
                <th className="transparent-header text-white fw-bold" style={{ minWidth: 200 }}>
                  Banner Preview
                </th>
                <th className="transparent-header text-center text-white fw-bold" style={{ width: 140 }}>
                  Status
                </th>
                <th className="transparent-header text-white fw-bold" style={{ width: 170 }}>
                  Created At
                </th>
                <th className="transparent-header text-white fw-bold" style={{ width: 170 }}>
                  Updated At
                </th>
                <th className="transparent-header text-center text-white fw-bold" style={{ width: 110 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="text-center py-5 transparent-header">
                    <div className="admin-loader"></div>
                    <div className="mt-2 small" style={{ color: "var(--text-secondary)" }}>
                      Loading homepage sliders...
                    </div>
                  </td>
                </tr>
              ) : filteredSliders.length > 0 ? (
                filteredSliders.map((slider) => (
                  <tr
                    key={slider.id}
                    className="table-themed-row"
                    style={{ borderBottom: "1px solid var(--divider)" }}
                  >
                    {/* Sort Order Badge */}
                    <td className="py-3 transparent-header text-center">
                      <span
                        className="badge"
                        style={{
                          background: "rgba(217, 255, 31, 0.12)",
                          color: "var(--accent-lime)",
                          border: "1px solid rgba(217, 255, 31, 0.3)",
                          borderRadius: 6,
                          fontSize: 13,
                          padding: "4px 10px",
                          fontWeight: 700,
                        }}
                      >
                        #{slider.sortOrder}
                      </span>
                    </td>

                    {/* Image Preview Thumbnail */}
                    <td className="py-3 transparent-header">
                      <div
                        className="d-flex align-items-center gap-3"
                        style={{ cursor: "pointer" }}
                        onClick={() => setPreviewImage(slider.imageUrl)}
                        title="Click to view full banner"
                      >
                        <div
                          style={{
                            width: 140,
                            height: 60,
                            borderRadius: 8,
                            overflow: "hidden",
                            background: "#000",
                            border: "1px solid var(--divider)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={slider.imageUrl}
                            alt={`Banner #${slider.sortOrder}`}
                            style={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                        <div className="d-flex flex-column">
                          <span
                            className="small text-truncate"
                            style={{
                              color: "var(--text-primary)",
                              fontFamily: "monospace",
                              fontSize: 11,
                              maxWidth: 240,
                            }}
                          >
                            {slider.storagePath || "banner.webp"}
                          </span>
                          <span
                            style={{
                              fontSize: 11,
                              color: "var(--accent-lime)",
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              marginTop: 2,
                            }}
                          >
                            <FaExternalLinkAlt size={9} /> Click to enlarge
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Active/Inactive Switch */}
                    <td className="py-3 transparent-header text-center">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <div className="form-check form-switch mb-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={Boolean(slider.isActive)}
                            disabled={togglingId === slider.id}
                            onChange={() => handleToggleActive(slider)}
                            style={{
                              width: "2.4em",
                              height: "1.2em",
                              cursor: togglingId === slider.id ? "not-allowed" : "pointer",
                              margin: 0,
                            }}
                            title={`Toggle slider to ${slider.isActive ? "Inactive" : "Active"}`}
                          />
                        </div>
                        <span
                          className={`fw-semibold small ${
                            slider.isActive ? "text-success" : "text-danger"
                          }`}
                          style={{ minWidth: 60, textAlign: "left" }}
                        >
                          {slider.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </td>

                    {/* Created Date */}
                    <td className="py-3 transparent-header small" style={{ color: "var(--text-secondary)" }}>
                      {formatDate(slider.createdAt)}
                    </td>

                    {/* Updated Date */}
                    <td className="py-3 transparent-header small" style={{ color: "var(--text-secondary)" }}>
                      {formatDate(slider.updatedAt)}
                    </td>

                    {/* Actions */}
                    <td className="py-3 transparent-header text-center">
                      <div className="d-flex align-items-center justify-content-center gap-2">
                        <button
                          className="btn btn-link p-1"
                          style={{
                            color: "var(--accent-lime)",
                            outline: "none",
                            boxShadow: "none",
                            borderRadius: 6,
                          }}
                          onClick={() => setEditingSlider(slider)}
                          title="Edit Slider"
                        >
                          <FaEdit size={16} />
                        </button>
                        <button
                          className="btn btn-link text-danger p-1"
                          style={{
                            outline: "none",
                            boxShadow: "none",
                            borderRadius: 6,
                          }}
                          onClick={() => setDeletingSlider(slider)}
                          title="Delete Slider"
                        >
                          <FaTrash size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                /* Empty state */
                <tr>
                  <td colSpan="6" className="text-center py-5 transparent-header">
                    <div
                      style={{
                        background: "var(--bg-surface)",
                        border: "1px dashed var(--divider)",
                        borderRadius: 16,
                        padding: "48px 24px",
                        maxWidth: 480,
                        margin: "0 auto",
                      }}
                    >
                      <div
                        style={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          background: "rgba(217, 255, 31, 0.1)",
                          color: "var(--accent-lime)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto 16px",
                        }}
                      >
                        <FaImages size={28} />
                      </div>
                      <h5 style={{ color: "var(--text-primary)", fontWeight: 700, marginBottom: 8 }}>
                        {statusFilter !== "all"
                          ? `No ${statusFilter} sliders found`
                          : "No Sliders Uploaded Yet"}
                      </h5>
                      <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 20 }}>
                        {statusFilter !== "all"
                          ? `Change your filter to view other sliders or upload a new one.`
                          : "Upload banner images to display them in the homepage carousel for your users."}
                      </p>
                      <button
                        className="btn btn-primary px-4 fw-bold"
                        style={{ borderRadius: 8, fontSize: 14 }}
                        onClick={() => setShowUploadModal(true)}
                      >
                        <FaPlus className="me-2" size={12} />
                        Upload New Slider
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Modal: Full Resolution Banner Viewer */}
        <Modal
          show={!!previewImage}
          onHide={() => setPreviewImage(null)}
          centered
          size="xl"
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title style={{ color: "var(--accent-lime)", fontSize: 16 }}>
              Banner Preview
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-3 text-center">
            {previewImage && (
              <img
                src={previewImage}
                alt="Enlarged banner preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "75vh",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
              />
            )}
          </Modal.Body>
        </Modal>

        {/* Modal: Upload New Slider */}
        <UploadSliderModal
          show={showUploadModal}
          onHide={() => setShowUploadModal(false)}
          onSuccess={(msg) => {
            showToast("success", msg);
            fetchSliders();
          }}
          existingSliders={sliders}
        />

        {/* Modal: Edit Slider */}
        <EditSliderModal
          slider={editingSlider}
          show={!!editingSlider}
          onHide={() => setEditingSlider(null)}
          onSuccess={(msg) => {
            showToast("success", msg);
            fetchSliders();
          }}
        />

        {/* Modal: Delete Slider Confirmation */}
        <DeleteSliderConfirmModal
          slider={deletingSlider}
          show={!!deletingSlider}
          onHide={() => setDeletingSlider(null)}
          onSuccess={(msg) => {
            showToast("success", msg);
            fetchSliders();
          }}
        />

        {/* Toast Notification */}
        <Toast toast={toast} />
      </Container>
    </section>
  );
};

export default SlidersManagement;
